import React from 'react';
import {
  FaClock,
  FaSave,
  FaExchangeAlt,
} from 'react-icons/fa';
import { Shift } from '../../shifts/services/ShiftService';
import { LaneTeam } from '../services/PlantafelService';
import styles from './PlantafelSidePanel.module.css';

interface SidePanelTimeEditProps {
  shift: Shift | null;
  editStartDate: string;
  editStartTime: string;
  editEndDate: string;
  editEndTime: string;
  setEditStartDate: (val: string) => void;
  setEditStartTime: (val: string) => void;
  setEditEndDate: (val: string) => void;
  setEditEndTime: (val: string) => void;
  hasTimeChanged: boolean;
  savingTime: boolean;
  handleSaveTime: () => void;
  getDayName: (dateStr: string) => string;
  allTeams: LaneTeam[];
  selectedTeamId: number | null;
  setSelectedTeamId: (id: number | null) => void;
  rescheduling: boolean;
  handleReschedule: () => void;
}

export function SidePanelTimeEdit({
  shift, editStartDate, editStartTime, editEndDate, editEndTime,
  setEditStartDate, setEditStartTime, setEditEndDate, setEditEndTime,
  hasTimeChanged, savingTime, handleSaveTime, getDayName,
  allTeams, selectedTeamId, setSelectedTeamId, rescheduling, handleReschedule,
}: SidePanelTimeEditProps): React.JSX.Element | null {
  if (!shift) return null;

  return (
    <>
      {/* Zeit & Datum */}
      <div className={styles.section} style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div className={styles.sectionTitle} style={{ marginBottom: 0 }}>
            <FaClock size={11} style={{ marginRight: '6px' }} />
            Zeit & Datum
          </div>
          {hasTimeChanged && (
            <button
              onClick={handleSaveTime}
              disabled={savingTime}
              className={styles.saveTimeButton}
              style={{
                background: savingTime ? '#ccc' : '#10b981',
                cursor: savingTime ? 'not-allowed' : 'pointer',
              }}
            >
              <FaSave size={10} />
              {savingTime ? '...' : 'Speichern'}
            </button>
          )}
        </div>

        <div className={styles.timeEditRow}>
          {/* Start */}
          <div className={styles.timeEditGroup}>
            <span className={styles.dayLabel}>{getDayName(editStartDate)}</span>
            <input
              type="date"
              value={editStartDate}
              onChange={(e) => setEditStartDate(e.target.value)}
              className={styles.compactDateInput}
            />
            <input
              type="time"
              value={editStartTime}
              onChange={(e) => setEditStartTime(e.target.value)}
              className={styles.compactTimeInput}
            />
          </div>

          <span className={styles.timeArrow}>&rarr;</span>

          {/* Ende */}
          <div className={styles.timeEditGroup}>
            <span className={styles.dayLabel}>{getDayName(editEndDate)}</span>
            <input
              type="date"
              value={editEndDate}
              onChange={(e) => setEditEndDate(e.target.value)}
              className={styles.compactDateInput}
            />
            <input
              type="time"
              value={editEndTime}
              onChange={(e) => setEditEndTime(e.target.value)}
              className={styles.compactTimeInput}
            />
          </div>
        </div>

        {/* Team info */}
        {(shift.work_area_team_name || (shift as any).work_area_team__name) && (
          <div className={styles.infoRow} style={{ marginTop: '8px' }}>
            <div
              className={styles.teamColorDot}
              style={{ background: shift.work_area__color || '#7c3aed' }}
            />
            <span style={{ fontSize: '13px', color: '#666' }}>
              Team: <strong style={{ color: '#021a4a' }}>
                {shift.work_area_team_name || (shift as any).work_area_team__name}
              </strong>
              {shift.work_area__name && (
                <span style={{ color: '#888' }}> ({shift.work_area__name})</span>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Team wechseln - nur für Job Shifts */}
      {shift.is_job_shift && allTeams.length > 0 && (
        <div className={styles.section} style={{ marginBottom: '16px' }}>
          <div className={styles.sectionTitle}>Team wechseln</div>
          <div className={styles.teamChangeRow}>
            <select
              value={selectedTeamId || ''}
              onChange={(e) => setSelectedTeamId(e.target.value ? parseInt(e.target.value, 10) : null)}
              className={styles.teamSelect}
            >
              <option value="">Team auswählen...</option>
              {allTeams
                .filter((t) => t.id !== (shift as any).work_area_team && t.id !== shift.work_area)
                .map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.work_area_name} &ndash; {team.name}
                  </option>
                ))}
            </select>
            <button
              onClick={handleReschedule}
              disabled={!selectedTeamId || rescheduling}
              className={styles.teamMoveButton}
              style={{
                background: !selectedTeamId || rescheduling ? '#e5e7eb' : '#7c3aed',
                color: !selectedTeamId || rescheduling ? '#9ca3af' : 'white',
                cursor: !selectedTeamId || rescheduling ? 'not-allowed' : 'pointer',
              }}
            >
              <FaExchangeAlt size={11} />
              {rescheduling ? '...' : 'Verschieben'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
